import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import zh from "./zh";
import en from "./en";

export const LOCALE_STORAGE_KEY = "lb.locale";
export const DEFAULT_LOCALE = "zh";
export const SUPPORTED_LOCALES = Object.freeze(["zh", "en"]);

const DICTIONARIES = Object.freeze({ zh, en });

export function normalizeLocale(value) {
  return SUPPORTED_LOCALES.includes(value) ? value : DEFAULT_LOCALE;
}

export function readLocale(storage) {
  try {
    if (!storage || typeof storage.getItem !== "function") {
      return DEFAULT_LOCALE;
    }
    return normalizeLocale(storage.getItem(LOCALE_STORAGE_KEY));
  } catch (_) {
    return DEFAULT_LOCALE;
  }
}

export function persistLocale(storage, locale) {
  const normalized = normalizeLocale(locale);

  if (!storage || typeof storage.setItem !== "function") {
    return normalized;
  }

  try {
    storage.setItem(LOCALE_STORAGE_KEY, normalized);
  } catch (_) {
    // Locale persistence is presentation-only and must never block the app.
  }

  return normalized;
}

function browserStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  return window.localStorage;
}

function resolveKey(dictionary, key) {
  return key.split(".").reduce(
    (value, part) =>
      value && Object.prototype.hasOwnProperty.call(value, part)
        ? value[part]
        : undefined,
    dictionary
  );
}

export function translate(locale, key) {
  const normalized = normalizeLocale(locale);
  const selected = resolveKey(DICTIONARIES[normalized], key);

  if (typeof selected === "string") return selected;

  const fallback = resolveKey(DICTIONARIES[DEFAULT_LOCALE], key);
  return typeof fallback === "string" ? fallback : key;
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => readLocale(browserStorage()));

  const setLocale = useCallback((nextLocale) => {
    const normalized = persistLocale(browserStorage(), nextLocale);
    setLocaleState(normalized);
  }, []);

  const t = useCallback(
    (key) => translate(locale, key),
    [locale]
  );

  const value = useMemo(
    () => Object.freeze({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return React.createElement(LocaleContext.Provider, { value }, children);
}

export function useI18n() {
  const value = useContext(LocaleContext);

  if (!value) {
    throw new Error("useI18n must be used within LocaleProvider");
  }

  return value;
}
